import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Where a typeahead's list goes: fixed to the screen, under its input (or
 * above when there's no room). An absolute list is clipped by a scrolling
 * container — inside the short "Start an audit" dialog it showed 3 of 13
 * stores and covered the dialog's own controls (round 1 2026-10-21).
 * Follows the input while the page or the dialog scrolls or the dialog
 * re-centres (content loading); hidden while the input is scrolled out of
 * view. Phones: measured against the VISUAL viewport, so an open keyboard
 * counts as "no room below".
 */
export function useFloatingList(anchor: RefObject<HTMLElement | null>, open: boolean): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    if (!open) {
      setStyle(undefined);
      return;
    }
    let last = '';
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vv = window.visualViewport;
      const top0 = vv ? vv.offsetTop : 0;
      const vh = vv ? vv.height : window.innerHeight;
      const below = top0 + vh - r.bottom - 8;
      const above = r.top - top0 - 8;
      const hidden = r.bottom < top0 || r.top > top0 + vh;
      const up = below < 160 && above > below;
      const maxHeight = Math.round(Math.max(120, Math.min(260, up ? above : below)));
      const next: CSSProperties = hidden
        ? { display: 'none' }
        : {
            position: 'fixed',
            left: Math.round(r.left),
            width: Math.round(r.width),
            right: 'auto',
            maxHeight,
            ...(up
              ? { top: 'auto', bottom: Math.round(window.innerHeight - r.top + 2) }
              : { top: Math.round(r.bottom + 2), bottom: 'auto' }),
          };
      // Scrolling the list itself fires this too: only re-render when the place changed.
      const key = JSON.stringify(next);
      if (key === last) return;
      last = key;
      setStyle(next);
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    // A dialog that grows (its content loading) re-centres and moves the input.
    const box = anchor.current?.closest('.modal') ?? null;
    const ro = typeof ResizeObserver !== 'undefined' && box ? new ResizeObserver(place) : null;
    if (ro && box) ro.observe(box);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
      ro?.disconnect();
    };
  }, [open, anchor]);
  return style;
}
